import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AppDispatch } from 'app/store';

import { GetFreshTokenFn } from 'components/ReCaptcha/hooks/useCaptchaSubmit';

import authService from 'features/auth/authService';
import { setCredentials } from 'features/auth/authSlice';
import { selectAppLocale } from 'features/locale/localeSelectors';

import useErrorCatch from 'hooks/useErrorCatch';

import { HOME } from 'constants/path';
import { EMAIL, NAME, PHONE } from 'constants/searchParams';

import SetPasswordForm, {
  SetPasswordFormValues
} from '../components/SetPasswordForm/SetPasswordForm';

const SetPasswordByWebsiteLink: FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const catchError = useErrorCatch();
  const locale = useSelector(selectAppLocale);
  const platformLanguage = locale.toUpperCase();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const email = searchParams.get(EMAIL);
  const name = searchParams.get(NAME);
  const phone = searchParams.get(PHONE);

  useEffect(() => {
    if (!email || !name || !phone) {
      catchError({ default: { message: t('error.message.wrongLink') } });
      navigate('/');
    }
  }, [email, name, phone, catchError, navigate]);

  const handleSetPassword = async (
    values: SetPasswordFormValues,
    getFreshToken: GetFreshTokenFn
  ) => {
    if (!email || !name || !phone) {
      return;
    }

    setIsLoading(true);
    try {
      const { password } = values;

      await authService.signUp({
        password,
        phone,
        email,
        name,
        recaptchaToken: await getFreshToken(),
        platformLanguage
      });

      const data = await authService.login({
        email,
        password,
        recaptchaToken: await getFreshToken()
      });

      dispatch(
        setCredentials({
          token: data.accessToken,
          region: data?.region || null
        })
      );

      navigate(HOME, { state: { firstTimeLogin: true } });
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.setupPassword'),
          description: t('error.failToSetupPassword')
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SetPasswordForm
      onSubmit={handleSetPassword}
      isLoading={isLoading}
      email={email || ''}
    />
  );
};

export default SetPasswordByWebsiteLink;
